import '@babel/polyfill';
import Vue from 'vue';
import * as Sentry from '@sentry/browser';
import * as Integrations from '@sentry/integrations';
import VueI18n from 'vue-i18n';
import VTooltip from 'v-tooltip';
import vueXlsxTable from 'vue-xlsx-table';
import VueSocketio from 'vue-socket.io-extended';
import io from 'socket.io-client';
import Notifications from 'vue-notification';
import velocity from 'velocity-animate';
import VueClipboard from 'vue-clipboard2';
import VueDragResize from 'vue-drag-resize';
import VueQrcode from '@xkeshi/vue-qrcode';
import Multiselect from 'vue-multiselect';
import VueYouTubeEmbed from 'vue-youtube-embed';
import Paginate from 'vuejs-paginate';
import VueScrollTo from 'vue-scrollto';
import jQuery from 'jquery';
import Popper from 'popper.js';
// eslint-disable-next-line
import Bootstrap from 'bootstrap';
// eslint-disable-next-line
import BootstrapVue from 'bootstrap-vue';
import { ResizeObserver } from 'vue-resize';
import Autocomplete from 'v-autocomplete';
import VueHtmlToPaper from 'vue-html-to-paper';
import { VueMasonryPlugin } from 'vue-masonry';

import {
	MdField,
	MdButton,
	MdIcon,
	MdList,
	MdMenu,
	MdDatepicker,
	MdDialog,
	MdCheckbox,
	MdSubheader,
	MdDivider,
	MdRadio,
	MdProgress,
} from 'vue-material/dist/components';
import 'vue-material/dist/vue-material.min.css';
import 'vue-material/dist/theme/default.css';
import 'vue-multiselect/dist/vue-multiselect.min.css';
// import 'bootstrap/dist/css/bootstrap.min.css';
// import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-vue/dist/bootstrap-vue.css';
import 'vue-resize/dist/vue-resize.css';
import 'v-autocomplete/dist/v-autocomplete.css';

// import './plugins/vuetify';
import App from './App.vue';
import router from './router';
import store from './store';
import lang from './lang';
import './registerServiceWorker';

// Mixins
import votingMixin from './mixins/voting-mixin';
import globalMixin from './mixins/global-mixin';
import suggestionMixin from './mixins/suggestion-mixin';
import agendaMixin from './mixins/agenda-mixin';
import MegaClickOutside from './directives/mega-click-outside';

Vue.mixin(globalMixin);
Vue.mixin(suggestionMixin);
Vue.mixin(votingMixin);
Vue.mixin(agendaMixin);

const htmlPaperOptions = {
	name: '_blank',
	specs: [
		'fullscreen=yes',
		'titlebar=yes',
		'scrollbars=yes',
	],
	styles: [
		'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
		'https://unpkg.com/kidlat-css/css/kidlat.css',
	],
	timeout: 1000, // default timeout before the print window appears
	autoClose: true, // if false, the window will not close after printing
	windowTitle: window.document.title, // override the window title
};

const SOCKET_URL = process.env.VUE_APP_SOCKET_URL;
const socket = io(SOCKET_URL, {
	// WARNING: in that case, there is no fallback to long-polling
	transports: ['websocket'], // or ['websocket', 'polling'], which is the same thing
});

Vue.config.productionTip = false;
Vue.use(VueSocketio, socket, { store });
Vue.use(VueI18n);
Vue.use(VTooltip);
Vue.use(MdField);
Vue.use(MdButton);
Vue.use(MdIcon);
Vue.use(MdList);
Vue.use(MdMenu);
Vue.use(MdDatepicker);
Vue.use(MdDialog);
Vue.use(MdCheckbox);
Vue.use(MdSubheader);
Vue.use(MdDivider);
Vue.use(MdRadio);
Vue.use(MdProgress);
Vue.use(Notifications, { velocity });
Vue.use(vueXlsxTable, { rABS: false });
Vue.use(VueClipboard);
Vue.use(VueYouTubeEmbed, { global: true, componentId: 'youtube-media' });
Vue.use(VueScrollTo);
Vue.use(BootstrapVue);
Vue.use(Autocomplete);
Vue.use(VueHtmlToPaper, htmlPaperOptions);
Vue.use(VueMasonryPlugin);

Vue.component(VueQrcode.name, VueQrcode);
Vue.component('vue-drag-resize', VueDragResize);
Vue.component('multiselect', Multiselect);
Vue.component('paginate', Paginate);
Vue.component('resize-observer', ResizeObserver);

Vue.directive('mega-click-outside', MegaClickOutside);

window.$ = jQuery;
window.jQuery = jQuery;
window.Popper = Popper;

/* Sentry */
const SENTRY_ENABLED = process.env.VUE_APP_SENTRY_ENABLED || true;
if (SENTRY_ENABLED !== 'false') {
	Sentry.init({
		dsn: 'https://c90a1dc30812459f982fe24f36c185fc@sentry.io/1434316',
		integrations: [
			new Integrations.Vue({
				Vue,
				attachProps: true,
			}),
		],
	});
}

/* Define guest url */
const guestURL = function (to) {
	const { path, params, name } = to;
	let isGuestURL = false;

	const urlGuest = {
		'/callback': '/callback',
		'/login/sms': '/login/sms',
		'/login/auto_login': '/login/auto_login',
		'/privacy-policy': '/privacy-policy',
		'/terms': '/terms',
		'/support': '/support',
	};

	const isGuest = path === urlGuest[path];
	const isEventDisplay = params.eventSlug !== undefined && params.uniqueCode !== undefined;
	const isEventDisplayVoting = params.eventSlug !== undefined && params.uniqueCode !== undefined && params.votingId !== undefined;
	const isEventDisplaySuggestion = params.eventSlug !== undefined && params.uniqueCode !== undefined && params.suggestionId !== undefined;
	const isLandingPageComponent = name === 'LandingPage';
	const isGuestValid = isGuest || isEventDisplay || isEventDisplayVoting || isEventDisplaySuggestion || isLandingPageComponent;
	if (isGuestValid) {
		isGuestURL = true;
	}
	return isGuestURL;
};

/* Route Middleware */
router.beforeEach((to, from, next) => {
	const { path } = to;
	const { isLoggedIn } = store.getters;

	const errors = [];
	const alreadyLoggedIn = path === '/login' && isLoggedIn;
	if (alreadyLoggedIn) {
		errors.push('already_logged_in');
	}
	const isNotAvailable = errors.length > 0;

	const isGuestURL = guestURL(to);
	if (isGuestURL) {
		next();
	} else if (path !== '/login' && path !== '/logout' && path !== '/password/reset' && !isLoggedIn) {
		next('/login');
	} else if (isNotAvailable) {
		next('/');
	} else if (path === '/') {
		next('/events');
	} else {
		next();
	}
});

/* Route Middleware - Before Each */
router.beforeEach((to, from, next) => {
	store.dispatch('navigate', { to });
	const title = to.meta.title || '';
	document.title = title;
	next();
});

/* Create VueI18n instance with options */
const i18n = new VueI18n({
	locale: store.getters.locale, // set locale
	messages: lang, // set locale lang
});

new Vue({
	render: h => h(App),
	router,
	store,
	i18n,
}).$mount('#app');
